import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { destroyKeepAliveForTab } from '../components/layout/keepAliveBridge'

/** Max tabs in the bar (includes Dashboard). Opening another asks which page to close. */
export const MAX_OPEN_TABS = 12

export interface Tab {
  id: string
  title: string
  basePath: string
  lastPath: string
  icon?: string
  closable?: boolean
  state?: Record<string, any>
  /** Used to close the first-opened page when the tab limit is hit. */
  openedAt?: number
}

export type TabDraft = Omit<Tab, 'closable' | 'state'> & { basePath: string; lastPath: string }

export function getOldestClosableTab(tabs: Tab[]): Tab | undefined {
  const closable = tabs.filter((t) => t.closable !== false)
  if (closable.length === 0) return undefined
  return closable.reduce((oldest, tab) => {
    const oldestAt = oldest.openedAt ?? Number.POSITIVE_INFINITY
    const tabAt = tab.openedAt ?? Number.POSITIVE_INFINITY
    if (tabAt === Number.POSITIVE_INFINITY && oldestAt === Number.POSITIVE_INFINITY) {
      return oldest
    }
    return tabAt < oldestAt ? tab : oldest
  })
}

interface TabState {
  tabs: Tab[]
  activeTabId: string
  pendingTab: TabDraft | null
  limitDialogReturn: { tabId: string; lastPath: string } | null
  addTab: (tab: TabDraft) => boolean
  removeTab: (tabId: string) => void
  removeTabs: (tabIds: string[]) => void
  closeOtherTabs: (tabId: string) => void
  /** side = visual left/right (accounts for RTL) */
  closeTabsToSide: (tabId: string, side: 'left' | 'right', isRTL: boolean) => void
  setActiveTab: (tabId: string) => void
  updateTabLastPath: (tabId: string, lastPath: string) => void
  setTabState: (tabId: string, state: Record<string, any>) => void
  clearTabs: () => void
  cancelPendingTab: () => void
  resolveTabLimit: (closeTabId?: string) => TabDraft | null
}

const DASHBOARD_TAB: Tab = {
  id: 'dashboard',
  title: 'Dashboard',
  basePath: '/dashboard',
  lastPath: '/dashboard',
  closable: false
}

export const useTabStore = create<TabState>()(
  persist(
    (set, get) => ({
      tabs: [DASHBOARD_TAB],
      activeTabId: 'dashboard',
      pendingTab: null,
      limitDialogReturn: null,

      addTab: (newTab) => {
        const { tabs, activeTabId } = get()
        const existingTab = tabs.find((t) => t.basePath === newTab.basePath)

        if (existingTab) {
          const clearPending = get().pendingTab?.basePath === existingTab.basePath
          const next = {
            tabs: tabs.map((t) =>
              t.id === existingTab.id
                ? {
                    ...t,
                    lastPath: newTab.lastPath || t.lastPath,
                    title: newTab.title || t.title,
                  }
                : t
            ),
            activeTabId: existingTab.id,
            ...(clearPending ? { pendingTab: null as null, limitDialogReturn: null as null } : {}),
          }
          set(next)
          return true
        }

        if (tabs.length >= MAX_OPEN_TABS) {
          const oldest = getOldestClosableTab(tabs)
          if (oldest) {
            destroyKeepAliveForTab(oldest.id)
            set({
              tabs: [
                ...tabs.filter((t) => t.id !== oldest.id),
                {
                  ...newTab,
                  closable: true,
                  openedAt: newTab.openedAt ?? Date.now(),
                },
              ],
              activeTabId: newTab.id,
              pendingTab: null,
              limitDialogReturn: null,
            })
            return true
          }
          const active = tabs.find((t) => t.id === activeTabId)
          set({
            pendingTab: newTab,
            limitDialogReturn: get().limitDialogReturn ?? {
              tabId: activeTabId,
              lastPath: active?.lastPath || '/dashboard',
            },
          })
          return false
        }

        const tabWithClosable: Tab = {
          ...newTab,
          closable: true,
          openedAt: newTab.openedAt ?? Date.now(),
        }
        set({
          tabs: [...tabs, tabWithClosable],
          activeTabId: newTab.id,
          pendingTab: null,
          limitDialogReturn: null,
        })
        return true
      },

      removeTab: (tabId) => {
        const { tabs, activeTabId } = get()
        if (tabs.length === 1 && tabs[0].id === 'dashboard') return
        if (tabs.find(t => t.id === tabId)?.closable === false) return

        const filteredTabs = tabs.filter((t) => t.id !== tabId)
        let newActiveTabId = activeTabId

        if (activeTabId === tabId) {
          newActiveTabId = filteredTabs[filteredTabs.length - 1].id
        }

        set({
          tabs: filteredTabs,
          activeTabId: newActiveTabId
        })
      },

      removeTabs: (tabIds) => {
        const { tabs, activeTabId } = get()
        const idsToRemove = new Set(tabIds.filter(id => {
          const tab = tabs.find(t => t.id === id)
          return tab && tab.closable !== false
        }))
        if (idsToRemove.size === 0) return

        const filteredTabs = tabs.filter((t) => !idsToRemove.has(t.id))
        let newActiveTabId = activeTabId

        if (idsToRemove.has(activeTabId)) {
          newActiveTabId = filteredTabs[filteredTabs.length - 1]?.id || 'dashboard'
        }

        set({
          tabs: filteredTabs.length > 0 ? filteredTabs : [DASHBOARD_TAB],
          activeTabId: newActiveTabId
        })
      },

      closeOtherTabs: (tabId) => {
        const { tabs } = get()
        const idsToClose = tabs
          .filter(t => t.id !== tabId && t.closable !== false)
          .map(t => t.id)
        get().removeTabs(idsToClose)
        set({ activeTabId: tabId })
      },

      closeTabsToSide: (tabId, side, isRTL) => {
        const { tabs } = get()
        const index = tabs.findIndex(t => t.id === tabId)
        if (index < 0) return

        // In RTL flex row, index 0 is on the visual right; in LTR it's on the left.
        const closingLowerIndices = isRTL ? side === 'right' : side === 'left'
        const idsToClose = tabs
          .filter((t, i) => {
            if (t.closable === false) return false
            return closingLowerIndices ? i < index : i > index
          })
          .map(t => t.id)

        get().removeTabs(idsToClose)
      },

      setActiveTab: (tabId) => set({ activeTabId: tabId }),

      updateTabLastPath: (tabId: string, lastPath: string) => {
        const { tabs } = get()
        set({
          tabs: tabs.map(t => t.id === tabId ? { ...t, lastPath } : t)
        })
      },

      setTabState: (tabId: string, state: Record<string, any>) => {
        const { tabs } = get()
        set({
          tabs: tabs.map(t => t.id === tabId ? { ...t, state: { ...t.state, ...state } } : t)
        })
      },

      clearTabs: () => set({
        tabs: [DASHBOARD_TAB],
        activeTabId: 'dashboard',
        pendingTab: null,
        limitDialogReturn: null,
      }),

      cancelPendingTab: () => set({ pendingTab: null, limitDialogReturn: null }),

      resolveTabLimit: (closeTabId) => {
        const { pendingTab, tabs } = get()
        if (!pendingTab) return null
        const target = closeTabId
          ? tabs.find((t) => t.id === closeTabId && t.closable !== false)
          : getOldestClosableTab(tabs)
        if (!target) return null
        get().removeTab(target.id)
        const toOpen = pendingTab
        set({ pendingTab: null, limitDialogReturn: null })
        get().addTab(toOpen)
        return toOpen
      },
    }),
    {
      name: 'tab-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        tabs: state.tabs,
        activeTabId: state.activeTabId,
      }),
      // Migrate old tabs to new structure
      migrate: (persistedState: any, version) => {
        if (version === 0 && persistedState?.state?.tabs) {
          return {
            ...persistedState,
            state: {
              ...persistedState.state,
              tabs: persistedState.state.tabs.map((tab: any) => ({
                ...tab,
                basePath: tab.path,
                lastPath: tab.path
              }))
            }
          }
        }
        return persistedState
      },
      version: 1
    }
  )
)
